// Sub-agent swarm: ReAct loop per leaf node (retrieve, read, critique, refine)
// with early stop on confidence. Emits state updates through the engine bus.

import { DEFAULT_CONFIG, type ResearchConfig } from "./config";
import type { AgentId, Claim, NodeId, PlanNode, RetrievedDoc, SubAgentState, ToolHint } from "./types";
import { adversarialCritique, isClaimCritical, selfConsistencyVote } from "./critic";
import { hybridRetrieve, pickEvidenceForQuestion, spinClaimText } from "./retrieval";
import { rand, sleep, uid } from "./util";

export interface RunSwarmArgs {
  leaves: PlanNode[];
  maxAgents: number;
  signal?: AbortSignal;
  config?: ResearchConfig;
  onSpawn?: (agent: SubAgentState) => Promise<void> | void;
  onUpdate?: (agentId: AgentId, patch: Partial<SubAgentState>) => Promise<void> | void;
  onAudit?: (entry: {
    kind: "swarm.spawn" | "tool.exec" | "thought.commit" | "critique" | "self_consistency" | "memory.write";
    agentId?: AgentId;
    nodeId?: NodeId;
    tool?: ToolHint;
    latencyMs?: number;
    payload?: unknown;
  }) => Promise<void> | void;
  onCost?: (deltaTokens: number, deltaCostUsd: number) => void;
}

function laySwarmPositions(n: number): Array<{ x: number; y: number }> {
  // Force-free spiral layout for nice initial render; UI may animate.
  const out: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < n; i++) {
    const angle = i * 2.39996; // golden angle
    const r = 0.18 + 0.42 * Math.sqrt(i / Math.max(1, n));
    out.push({ x: 0.5 + r * Math.cos(angle), y: 0.5 + r * Math.sin(angle) });
  }
  return out;
}

export async function runSwarm(args: RunSwarmArgs): Promise<SubAgentState[]> {
  const cfg = args.config ?? DEFAULT_CONFIG;
  const work = args.leaves.slice(0, Math.max(cfg.swarm.minAgents, Math.min(args.maxAgents, cfg.swarm.maxAgents)));
  const positions = laySwarmPositions(work.length);
  const agents: SubAgentState[] = work.map((leaf, i) => ({
    id: uid("a"),
    nodeId: leaf.id,
    label: leaf.question.slice(0, 64),
    status: "spawning",
    iteration: 0,
    maxIterations: cfg.swarm.iterations.default,
    confidence: 0,
    tokensUsed: 0,
    costUsd: 0,
    startedAt: performance.now(),
    latencyMs: 0,
    toolsActive: leaf.tools.slice(),
    claims: [],
    evidence: [],
    x: positions[i].x,
    y: positions[i].y,
  }));

  for (const a of agents) {
    await args.onSpawn?.(a);
    await args.onAudit?.({ kind: "swarm.spawn", agentId: a.id, nodeId: a.nodeId, payload: { label: a.label } });
  }

  // Run agents in parallel — each is an async ReAct loop.
  await Promise.all(agents.map((a) => runAgent(a, work.find((l) => l.id === a.nodeId)!, args, cfg)));
  return agents;
}

async function runAgent(
  a: SubAgentState,
  leaf: PlanNode,
  args: RunSwarmArgs,
  cfg: ResearchConfig,
) {
  const update = async (patch: Partial<SubAgentState>) => {
    Object.assign(a, patch);
    await args.onUpdate?.(a.id, patch);
  };

  try {
    for (let it = 1; it <= a.maxIterations; it++) {
      if (args.signal?.aborted) {
        await update({ status: "stopped", endedAt: performance.now(), latencyMs: performance.now() - a.startedAt });
        return;
      }
      await update({ iteration: it, status: "retrieving" });

      // 1) Retrieval
      const ret = await hybridRetrieve({
        query: leaf.question,
        signal: args.signal,
        config: cfg,
        onLayer: async (layer, ms, n) => {
          await args.onAudit?.({
            kind: "tool.exec", agentId: a.id, nodeId: a.nodeId,
            tool: layer === "graph" ? "graph_rag" : layer,
            latencyMs: Math.round(ms),
            payload: { layer, count: n, iter: it },
          });
        },
        onTool: async (tool, ms) => {
          await args.onAudit?.({ kind: "tool.exec", agentId: a.id, nodeId: a.nodeId, tool, latencyMs: Math.round(ms) });
        },
      });

      const newEvidence = ret.docs;
      const evidenceMap = new Map<string, RetrievedDoc>();
      for (const d of [...a.evidence, ...newEvidence]) evidenceMap.set(d.id, d);
      const evidence = Array.from(evidenceMap.values());

      // 2) Read + propose claims (1-3 per round)
      await update({ status: "reading" });
      await sleep(rand(180, 420), args.signal);
      const top = pickEvidenceForQuestion(leaf.question, newEvidence, Math.min(3, newEvidence.length));
      const proposed: Claim[] = top.map((d) => ({
        id: uid("c"),
        text: spinClaimText(leaf.question, d),
        evidenceIds: [d.id],
        confidence: Math.min(0.95, 0.45 + d.score * 0.6 + Math.random() * 0.1),
        status: "proposed",
      }));
      for (const p of proposed) {
        await args.onAudit?.({
          kind: "thought.commit", agentId: a.id, nodeId: a.nodeId,
          payload: { claim: p.text, evidence: p.evidenceIds, confidence: p.confidence },
        });
      }

      // 3) Critique adversarial
      await update({ status: "critiquing" });
      const critiqued = await adversarialCritique({ claims: proposed, signal: args.signal, config: cfg });
      for (const c of critiqued) {
        await args.onAudit?.({
          kind: "critique", agentId: a.id, nodeId: a.nodeId,
          payload: { claim: c.text.slice(0, 120), confidence: c.confidence, status: c.status, rationale: c.rationale },
        });
      }

      // 4) Self-consistency on critical claims
      const critical = critiqued.filter(isClaimCritical);
      const nonCritical = critiqued.filter((c) => !isClaimCritical(c));
      let voted: Claim[] = critiqued;
      if (critical.length) {
        const v = await selfConsistencyVote(critical, args.signal, cfg);
        for (const c of v) {
          await args.onAudit?.({
            kind: "self_consistency", agentId: a.id, nodeId: a.nodeId,
            payload: { claim: c.text.slice(0, 120), agreed: c.agreedN, of: c.sampledN, confidence: c.confidence },
          });
        }
        voted = [...nonCritical, ...v];
      }

      // 5) Refine: merge claims, drop rejected
      await update({ status: "refining" });
      const merged = mergeClaims([...a.claims, ...voted]);
      const conf = aggregateConfidence(merged);

      // accumulated cost (simulated: tokens roughly proportional to evidence size)
      const tokensThisRound = Math.round(800 + 220 * top.length + Math.random() * 500);
      const costThisRound = (tokensThisRound / 1000) * cfg.cost.perKTokens + 4 * cfg.cost.perToolCall;
      args.onCost?.(tokensThisRound, costThisRound);

      await update({
        evidence,
        claims: merged,
        confidence: conf,
        tokensUsed: a.tokensUsed + tokensThisRound,
        costUsd: a.costUsd + costThisRound,
        latencyMs: performance.now() - a.startedAt,
      });

      await args.onAudit?.({
        kind: "memory.write", agentId: a.id, nodeId: a.nodeId,
        payload: { claims: merged.length, confidence: Number(conf.toFixed(3)) },
      });

      // Early stop
      if (conf >= cfg.swarm.earlyStopConfidence) break;
    }

    await update({
      status: "done",
      endedAt: performance.now(),
      latencyMs: performance.now() - a.startedAt,
    });
  } catch (e) {
    await update({
      status: args.signal?.aborted ? "stopped" : "failed",
      endedAt: performance.now(),
      latencyMs: performance.now() - a.startedAt,
    });
  }
}

function mergeClaims(claims: Claim[]): Claim[] {
  // Drop rejected; dedup by first 64 chars of normalized text; keep max confidence.
  const map = new Map<string, Claim>();
  for (const c of claims) {
    if (c.status === "rejected") continue;
    const key = c.text.toLowerCase().replace(/\s+/g, " ").slice(0, 80);
    const ex = map.get(key);
    if (!ex || c.confidence > ex.confidence) map.set(key, c);
  }
  return Array.from(map.values()).sort((a, b) => b.confidence - a.confidence);
}

function aggregateConfidence(claims: Claim[]): number {
  if (!claims.length) return 0;
  const accepted = claims.filter((c) => c.status !== "rejected");
  if (!accepted.length) return 0;
  const top = accepted.slice(0, 6);
  return top.reduce((s, c) => s + c.confidence, 0) / top.length;
}
